import { useCallback, useEffect, useRef, useState } from 'react';
import { BASE, CREW, cellFor } from './rtbBoard';
import { sfx } from './rtbAudio';
import { diceBus } from './RtbDice';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/rtb`;
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
const positionsOf = (teams) => Object.fromEntries(Object.entries(teams).map(([t, s]) => [t, s.pieces.map((p) => p.progress)]));

async function call(url, body) {
  const res = await fetch(url, body ? { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) } : undefined);
  const data = await res.json().catch(() => ({}));
  if (!res.ok) { const e = new Error(data.detail || 'Request failed'); e.status = res.status; throw e; }
  return data;
}

export function useRtbMatch(gameId) {
  const [match, setMatch] = useState(null);
  const [myTeam, setMyTeam] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [display, setDisplay] = useState(null);
  const [turn, setTurn] = useState(null);
  const [dice, setDice] = useState({ value: 6, rolling: false, team: null });
  const [toasts, setToasts] = useState([]);
  const [combat, setCombat] = useState(null);
  const [fx, setFx] = useState(null);
  const [animating, setAnimating] = useState(false);
  const [speed, setSpeedState] = useState(() => Number(localStorage.getItem('rtb_speed')) || 1);
  const setSpeed = (v) => { localStorage.setItem('rtb_speed', String(v)); setSpeedState(v); };
  const [auto, setAuto] = useState(false);
  const [camera, setCamera] = useState(false);
  const myTeamRef = useRef(null);
  myTeamRef.current = myTeam;

  const queue = useRef([]);
  const running = useRef(false);
  const lastSeq = useRef(0);
  const latest = useRef(null);
  const factor = useRef(1);
  const combatDone = useRef(null);
  factor.current = 1 / speed;
  const w = (ms) => sleep(ms * factor.current);

  const toast = useCallback((text, tone = 'info') => {
    const id = Math.random();
    setToasts((t) => [...t.slice(-3), { id, text, tone }]);
    setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), 2600);
  }, []);

  const place = (team, piece, progress) =>
    setDisplay((d) => ({ ...d, [team]: d[team].map((p, i) => (i === piece ? progress : p)) }));

  const handle = async (ev) => {
    const T = (t) => (t || '').toUpperCase();
    switch (ev.type) {
      case 'roll': {
        const mine = ev.team === myTeamRef.current;
        setDice((d) => ({ ...d, rolling: true, team: ev.team }));
        if (mine) { setCamera(true); await sleep(460); }
        if (diceBus.throw) await diceBus.throw(ev.value, ev.seq * 7919 + ev.value, mine ? Math.max(0.6, factor.current) : factor.current);
        else await w(900);
        setDice({ value: ev.value, rolling: false, team: ev.team });
        await w(mine ? 700 : 300);
        if (mine) { setCamera(false); await sleep(480); }
        break;
      }
      case 'move':
        for (const p of ev.path) { place(ev.team, ev.piece, p); sfx.step(); await w(250); }
        break;
      case 'capture':
        setFx({ key: ev.seq, cell: cellFor(ev.team, ev.from, ev.victim), color: ev.by });
        sfx.capture();
        toast(`${T(ev.by)} captured ${T(ev.team)}'s ${CREW[ev.victim]}${ev.bounty ? ` · +$${ev.bounty.toLocaleString()} bounty` : ''}${ev.streak > 1 ? ` · streak ${ev.streak}/3` : ''}`, 'capture');
        await w(420);
        place(ev.team, ev.victim, BASE);
        await w(750);
        break;
      case 'combat':
        sfx.combat();
        setCombat(ev);
        await Promise.race([new Promise((r) => { combatDone.current = r; }), sleep(4500)]);
        setCombat(null);
        if (ev.sent_back) { place(ev.sent_back.team, ev.sent_back.piece, BASE); await w(700); }
        break;
      case 'tile':
        toast(`${T(ev.team)} ${CREW[ev.piece]}: ${ev.text}${ev.cash ? ` ${ev.cash > 0 ? '+' : '-'}$${Math.abs(ev.cash).toLocaleString()}` : ''}${ev.heat ? ` · +${ev.heat} heat` : ''}`, ev.cash < 0 || ev.heat ? 'bad' : 'good');
        await w(500);
        break;
      case 'home':
        sfx.home();
        toast(`${T(ev.team)} ${CREW[ev.piece]} made it HOME (${ev.count}/4)`, 'good');
        await w(600);
        break;
      case 'win':
        sfx.win();
        break;
      case 'turn':
        setTurn(ev.team);
        await w(200);
        break;
      default:
        if (ev.reason || ev.type === 'joined' || ev.type === 'left') {
          toast(ev.reason ? `${T(ev.team)}: ${ev.reason}` : `${T(ev.team)} ${ev.type === 'joined' ? `taken by ${ev.name}` : 'is now AI'}`);
          await w(650);
        }
    }
  };

  const pump = async () => {
    if (running.current) return;
    running.current = true;
    setAnimating(true);
    while (queue.current.length) await handle(queue.current.shift());
    if (latest.current) { setDisplay(positionsOf(latest.current.teams)); setTurn(latest.current.turn); }
    running.current = false;
    setAnimating(false);
  };

  const ingest = useCallback((data, initial = false) => {
    const m = data.match;
    latest.current = m;
    setMatch(m);
    setMyTeam(data.my_team || null);
    if (!m) { lastSeq.current = 0; setDisplay(null); return; }
    if (initial || !display) {
      queue.current = [];
      lastSeq.current = m.seq;
      setDisplay(positionsOf(m.teams));
      setTurn(m.turn);
      return;
    }
    const fresh = (m.events || []).filter((e) => e.seq > lastSeq.current);
    if (fresh.length) { lastSeq.current = fresh[fresh.length - 1].seq; queue.current.push(...fresh); pump(); }
    else if (!running.current) { setDisplay(positionsOf(m.teams)); setTurn(m.turn); }
  }, [display]); // eslint-disable-line react-hooks/exhaustive-deps

  const refresh = useCallback(async (initial = false) => {
    try {
      const data = await call(`${API}/${gameId}/match?since=${initial ? 0 : lastSeq.current}`);
      ingest(data, initial);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [gameId, ingest]);

  useEffect(() => { refresh(true); }, [gameId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const id = setInterval(() => {
      const m = latest.current;
      if (!m || busy || running.current) return;
      if (m.status === 'lobby' || (m.status === 'playing' && m.turn !== myTeam)) refresh();
    }, 2500);
    return () => clearInterval(id);
  }, [busy, myTeam, refresh]);

  const act = useCallback(async (path, extra = {}, initial = false) => {
    if (busy || running.current) return;
    setBusy(true); setError(null);
    try {
      const body = latest.current ? { version: latest.current.version, since: lastSeq.current, ...extra } : extra;
      const data = await call(`${API}/${gameId}/${path}`, body);
      ingest(data, initial);
    } catch (e) {
      if (e.status === 409) await refresh(); else setError(e.message);
    } finally { setBusy(false); }
  }, [busy, gameId, ingest, refresh]);

  return {
    match, myTeam, loading, busy, error, setError, display, turn, dice, toasts, combat, fx, animating, speed, setSpeed, auto, setAuto, camera,
    advance: () => act('advance'),
    dismissCombat: () => combatDone.current && combatDone.current(),
    roll: () => act('roll'),
    move: (piece) => act('move', { piece }),
    endTurn: () => act('end-turn'),
    start: () => act('start'),
    create: (opts) => act('create', opts, true),
    join: (code) => act('join', { code }, true),
    leave: async () => { await call(`${API}/${gameId}/leave`, {}); latest.current = null; setAuto(false); ingest({ match: null }, true); },
  };
}
